import { useState, useRef, useEffect, useCallback } from 'react';
import { playCountdown, playIntervalBeep } from '../lib/audio/beeps';

export const useTimedDrill = ({
  currentDrill,
  isRunning,
  elapsedSeconds,
  onDrillStart,
  audioContext
}) => {
  const [timingEnabledState, setTimingEnabledState] = useState(false);
  const [timingValuesState, setTimingValuesState] = useState([]);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [countdownDisplay, setCountdownDisplay] = useState(null);

  const countdownTimeoutsRef = useRef([]);
  const nextBeepIndexRef = useRef(0);
  const remainingToNextBeepRef = useRef(null);
  const scheduledBeepTimeoutRef = useRef(null);
  const nextBeepAtRef = useRef(null);
  const latestElapsedRef = useRef(elapsedSeconds);
  const isRunningRef = useRef(isRunning);
  const previousIsRunningRef = useRef(isRunning);
  const countdownActiveRef = useRef(false);
  const audioCtxRef = useRef(null);
  const externalAudioContextRef = useRef(null);
  const createdAudioContextRef = useRef(false);
  const startTimingLayerRef = useRef(() => {});

  useEffect(() => {
    latestElapsedRef.current = elapsedSeconds;
  }, [elapsedSeconds]);

  useEffect(() => {
    countdownActiveRef.current = isCountdownActive;
  }, [isCountdownActive]);

  useEffect(() => {
    if (audioContext && typeof audioContext === 'object' && 'current' in audioContext) {
      externalAudioContextRef.current = audioContext;
      audioCtxRef.current = audioContext.current ?? audioCtxRef.current;
      createdAudioContextRef.current = false;
    } else if (audioContext) {
      audioCtxRef.current = audioContext;
      createdAudioContextRef.current = false;
    }
  }, [audioContext]);

  const clearCountdownTimers = useCallback(() => {
    countdownTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    countdownTimeoutsRef.current = [];
  }, []);

  const resetCountdownState = useCallback(() => {
    setIsCountdownActive(false);
    setCountdownDisplay(null);
  }, []);

  const clearScheduledBeep = useCallback(() => {
    if (scheduledBeepTimeoutRef.current) {
      clearTimeout(scheduledBeepTimeoutRef.current);
    }
    scheduledBeepTimeoutRef.current = null;
    nextBeepAtRef.current = null;
  }, []);

  const computeCumulativeTimings = useCallback(() => {
    if (!currentDrill?.timings?.length || !timingValuesState.length) return [];

    const sanitized = timingValuesState.map((value, index) => {
      if (value === '' || value === null || typeof value === 'undefined') {
        return currentDrill.timings[index]?.defaultSeconds ?? 0;
      }
      const numeric = parseInt(value, 10);
      if (Number.isNaN(numeric) || numeric <= 0) {
        return currentDrill.timings[index]?.defaultSeconds ?? 0;
      }
      return numeric;
    });

    const cumulative = [];
    sanitized.reduce((acc, seconds) => {
      const nextValue = acc + seconds;
      cumulative.push(nextValue);
      return nextValue;
    }, 0);

    return cumulative;
  }, [currentDrill, timingValuesState]);

  const ensureAudioContext = useCallback(async () => {
    if (typeof window === 'undefined') {
      return null;
    }

    let context = audioCtxRef.current;
    if (!context || context.state === 'closed') {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        return null;
      }
      context = new AudioContextClass();
      audioCtxRef.current = context;
      createdAudioContextRef.current = true;
      if (externalAudioContextRef.current) {
        externalAudioContextRef.current.current = context;
      }
    }

    if (context?.state === 'suspended') {
      try {
        await context.resume();
      } catch (error) {
        console.warn('Failed to resume AudioContext', error);
      }
    }

    return context ?? null;
  }, []);

  const scheduleNextBeepWithDelay = useCallback((delayMs) => {
    if (!timingEnabledState || !currentDrill?.timings?.length) return;

    const safeDelay = Math.max(0, delayMs);
    nextBeepAtRef.current = Date.now() + safeDelay;

    const timeoutId = setTimeout(() => {
      void (async () => {
        const context = await ensureAudioContext();
        if (context) {
          playIntervalBeep(context);
        }

        clearScheduledBeep();
        nextBeepIndexRef.current += 1;
        startTimingLayerRef.current();
      })();
    }, safeDelay);

    scheduledBeepTimeoutRef.current = timeoutId;
  }, [timingEnabledState, currentDrill, ensureAudioContext, clearScheduledBeep]);

  const startTimingLayer = useCallback(({ resumeFromPause = false } = {}) => {
    if (!timingEnabledState || !currentDrill?.timings?.length) {
      clearScheduledBeep();
      return;
    }

    const cumulativeTimings = computeCumulativeTimings();
    if (!cumulativeTimings.length) {
      clearScheduledBeep();
      return;
    }

    if (resumeFromPause && remainingToNextBeepRef.current != null) {
      const delayMs = remainingToNextBeepRef.current;
      remainingToNextBeepRef.current = null;
      scheduleNextBeepWithDelay(delayMs);
      return;
    }

    const elapsed = latestElapsedRef.current;
    while (
      nextBeepIndexRef.current < cumulativeTimings.length &&
      cumulativeTimings[nextBeepIndexRef.current] <= elapsed
    ) {
      nextBeepIndexRef.current += 1;
    }

    if (nextBeepIndexRef.current >= cumulativeTimings.length) {
      clearScheduledBeep();
      remainingToNextBeepRef.current = null;
      return;
    }

    const nextSeconds = cumulativeTimings[nextBeepIndexRef.current];
    const delayMs = Math.max(0, (nextSeconds - elapsed) * 1000);
    scheduleNextBeepWithDelay(delayMs);
  }, [timingEnabledState, currentDrill, computeCumulativeTimings, clearScheduledBeep, scheduleNextBeepWithDelay]);

  startTimingLayerRef.current = startTimingLayer;

  const stopTimingInternal = useCallback(() => {
    clearScheduledBeep();
    remainingToNextBeepRef.current = null;
    nextBeepIndexRef.current = 0;
  }, [clearScheduledBeep]);

  const pauseTimingInternal = useCallback(() => {
    if (!timingEnabledState || !currentDrill?.timings?.length) {
      clearScheduledBeep();
      return;
    }

    if (nextBeepAtRef.current) {
      remainingToNextBeepRef.current = Math.max(0, nextBeepAtRef.current - Date.now());
    } else {
      remainingToNextBeepRef.current = null;
    }

    clearScheduledBeep();
  }, [timingEnabledState, currentDrill, clearScheduledBeep]);

  const pauseTiming = useCallback(() => {
    pauseTimingInternal();
    if (countdownActiveRef.current) {
      clearCountdownTimers();
      resetCountdownState();
    }
  }, [pauseTimingInternal, clearCountdownTimers, resetCountdownState]);

  const resumeTiming = useCallback(() => {
    if (timingEnabledState && currentDrill?.timings?.length) {
      startTimingLayer({ resumeFromPause: true });
    }
  }, [timingEnabledState, currentDrill, startTimingLayer]);

  const stopTiming = useCallback(() => {
    stopTimingInternal();
    clearCountdownTimers();
    resetCountdownState();
  }, [stopTimingInternal, clearCountdownTimers, resetCountdownState]);

  const handleTimingValueChange = useCallback((index, value) => {
    setTimingValuesState((prev) => {
      const updated = [...prev];
      const parsed = parseInt(value, 10);
      if (value === '' || Number.isNaN(parsed)) {
        updated[index] = '';
      } else {
        updated[index] = Math.max(0, parsed);
      }
      return updated;
    });

    if (!isRunningRef.current && !countdownActiveRef.current) {
      remainingToNextBeepRef.current = null;
      nextBeepIndexRef.current = 0;
      clearScheduledBeep();
    }
  }, [clearScheduledBeep]);

  const setTimingValues = useCallback((valueOrUpdater) => {
    setTimingValuesState((prev) => {
      const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
      if (!isRunningRef.current && !countdownActiveRef.current) {
        remainingToNextBeepRef.current = null;
        nextBeepIndexRef.current = 0;
        clearScheduledBeep();
      }
      return next;
    });
  }, [clearScheduledBeep]);

  const setTimingEnabled = useCallback((valueOrUpdater) => {
    setTimingEnabledState((prev) => {
      const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
      if (next === prev) {
        return prev;
      }

      if (!next) {
        stopTimingInternal();
        if (countdownActiveRef.current) {
          clearCountdownTimers();
          resetCountdownState();
        }
      } else {
        remainingToNextBeepRef.current = null;
        nextBeepIndexRef.current = 0;
      }

      return next;
    });
  }, [stopTimingInternal, clearCountdownTimers, resetCountdownState]);

  const startCountdownAndDrill = useCallback(async () => {
    if (countdownActiveRef.current) {
      return;
    }

    const isResuming = latestElapsedRef.current > 0;

    const beginDrill = async () => {
      clearCountdownTimers();
      resetCountdownState();

      await onDrillStart?.();

      if (timingEnabledState && currentDrill?.timings?.length) {
        if (!isResuming) {
          nextBeepIndexRef.current = 0;
          remainingToNextBeepRef.current = null;
        }
        startTimingLayer({ resumeFromPause: isResuming });
      } else {
        stopTimingInternal();
      }
    };

    if (timingEnabledState && currentDrill?.timings?.length) {
      const context = await ensureAudioContext();
      if (context) {
        setIsCountdownActive(true);
        setCountdownDisplay('3');
        countdownActiveRef.current = true;

        clearCountdownTimers();
        playCountdown(context);

        const countdownSteps = [
          { label: '2', delay: 1000 },
          { label: '1', delay: 2000 },
          { label: 'GO', delay: 3000 }
        ];

        countdownSteps.forEach(({ label, delay }) => {
          const timeoutId = setTimeout(() => {
            setCountdownDisplay(label);
          }, delay);
          countdownTimeoutsRef.current.push(timeoutId);
        });

        const startTimeout = setTimeout(() => {
          void beginDrill();
        }, 3500);
        countdownTimeoutsRef.current.push(startTimeout);
        return;
      }
    }

    await beginDrill();
  }, [
    timingEnabledState,
    currentDrill,
    ensureAudioContext,
    clearCountdownTimers,
    resetCountdownState,
    onDrillStart,
    startTimingLayer,
    stopTimingInternal
  ]);

  useEffect(() => {
    if (currentDrill?.timings?.length) {
      setTimingValuesState(currentDrill.timings.map((phase) => phase.defaultSeconds));
    } else {
      setTimingValuesState([]);
    }

    setTimingEnabledState(false);
    stopTimingInternal();
    clearCountdownTimers();
    resetCountdownState();
    nextBeepIndexRef.current = 0;
    remainingToNextBeepRef.current = null;
  }, [currentDrill?.id, stopTimingInternal, clearCountdownTimers, resetCountdownState]);

  useEffect(() => {
    isRunningRef.current = isRunning;
    const wasRunning = previousIsRunningRef.current;

    if (!timingEnabledState || !currentDrill?.timings?.length) {
      if (!timingEnabledState) {
        stopTimingInternal();
      }
      previousIsRunningRef.current = isRunning;
      return;
    }

    if (isRunning && !wasRunning) {
      startTimingLayer({ resumeFromPause: true });
    } else if (!isRunning && wasRunning) {
      pauseTimingInternal();
    }

    previousIsRunningRef.current = isRunning;
  }, [isRunning, timingEnabledState, currentDrill, startTimingLayer, pauseTimingInternal, stopTimingInternal]);

  useEffect(() => () => {
    clearCountdownTimers();
    clearScheduledBeep();
    if (createdAudioContextRef.current && audioCtxRef.current && typeof audioCtxRef.current.close === 'function') {
      try {
        audioCtxRef.current.close();
      } catch (error) {
        console.warn('Failed to close AudioContext', error);
      }
    }
  }, [clearCountdownTimers, clearScheduledBeep]);

  return {
    timingEnabled: timingEnabledState,
    setTimingEnabled,
    timingValues: timingValuesState,
    setTimingValues,
    handleTimingValueChange,
    startCountdownAndDrill,
    pauseTiming,
    resumeTiming,
    stopTiming,
    isCountdownActive,
    countdownDisplay
  };
};

